import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import is from '@sindresorhus/is';
import { Fragment, useContext } from 'react';
import { Controller } from 'react-hook-form';
import Maybe from 'true-myth/maybe';

import { SkillList } from './SkillList';
import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    filterStateComponentMultiChipSelectLiteralSchema,
    filterStatePercentageRangeSchema,
    filterStateSkillSchema,
} from '../../../../../shared/source/data-contract/filter-state';
import { DateRangePicker } from '../../../design/components/form-fields/DateRangePicker';
import { Select } from '../../../design/components/form-fields/Select';
import { SkillSearch } from '../form/skills-field/SkillsSearch';
import { ReactHookFormAutocompleteField } from '../react-hook-form-fields/ReactHookFormAutocompleteField';
import { ReactHookFormMultiChipSelectorField } from '../react-hook-form-fields/ReactHookFormMultiChipSelectorField';

import type {
    FilterState,
    FilterStateBoolean,
    FilterStateDate,
    FilterStateInteger,
    FilterStatePercentage,
    FilterStateSkills,
    FilterStateString,
    FilterStateUser,
    FilterWithFilterStates,
} from '../../../../../shared/source/data-contract/filter-state';
import type { SkillShort } from '../../../../../shared/source/data-contract/skill';
import type { DateRange } from '@mui/x-date-pickers-pro';
import type { ChangeEventHandler, FunctionComponent } from 'react';
import type { Control } from 'react-hook-form';

type FieldName = `${number}.filterStates.${number}.value`;

type FilterInput<T extends FilterState = FilterState> = {
    readonly control: Control<readonly FilterWithFilterStates[]>;
    readonly fieldName: FieldName;
    readonly filterState: T;
};

const BooleanField: FunctionComponent<FilterInput<FilterStateBoolean>> = ({ control, fieldName, filterState }) => {
    const { translate } = useContext(ApplicationContext);

    return (
        <Controller
            control={control}
            name={fieldName}
            render={({ field, fieldState }) => {
                const value = is.boolean(field.value) ? String(field.value) : undefined;

                return (
                    <Select
                        helperText={fieldState.error?.message}
                        isError={is.nonEmptyString(fieldState.error?.message)}
                        label={filterState.label}
                        name={field.name}
                        options={[
                            {
                                key: 'none',
                                label: translate('common.no-selection'),
                                value: '',
                            },
                            {
                                key: 'true',
                                label: translate('common.yes'),
                                value: 'true',
                            },
                            {
                                key: 'false',
                                label: translate('common.no'),
                                value: 'false',
                            },
                        ]}
                        selectedValue={value}
                        onChange={(e) => {
                            const value = e.target.value;

                            field.onChange(is.emptyString(value) ? undefined : value === 'true');
                        }}
                    />
                );
            }}
        />
    );
};

const isStringArray = (value: unknown | readonly unknown[]): value is readonly string[] => is.array(value, is.string);

const SkillField: FunctionComponent<FilterInput<FilterStateSkills>> = ({ control, fieldName, filterState }) => (
    <Controller
        control={control}
        name={fieldName}
        render={({ field }) => {
            const { value } = field;

            const onAddSkill = (skill: SkillShort): void => {
                if (is.nullOrUndefined(value)) {
                    field.onChange([skill.id]);
                } else if (isStringArray(value)) {
                    field.onChange([...value, skill.id]);
                }
            };
            const onRemoveSkill = (skillId: string): void => {
                if (isStringArray(value)) {
                    field.onChange(value.filter((id) => id !== skillId));
                }
            };

            const skillIdsParsed = filterStateSkillSchema.shape.value
                .transform((skillIds) => {
                    return Maybe.of(skillIds);
                })
                .safeParse(value);
            const skillIds = skillIdsParsed.success ? skillIdsParsed.data.unwrapOr([]) : [];

            return (
                <Fragment>
                    {is.nonEmptyArray(skillIds) && (
                        <SkillList label={filterState.label} skillIds={skillIds} onRemoveSkill={onRemoveSkill} />
                    )}
                    <SkillSearch excludedSkillIds={skillIds} onAddSkill={onAddSkill} />
                </Fragment>
            );
        }}
    />
);

const StringField: FunctionComponent<FilterInput<FilterStateString>> = ({ control, fieldName, filterState }) => {
    const { component, label, options } = filterState;

    if (is.emptyArray(options)) {
        return null;
    }

    if (component === filterStateComponentMultiChipSelectLiteralSchema.value) {
        return (
            <ReactHookFormMultiChipSelectorField control={control} label={label} name={fieldName} options={options} />
        );
    }

    return <ReactHookFormAutocompleteField control={control} label={label} name={fieldName} options={options} />;
};

const UserField: FunctionComponent<FilterInput<FilterStateUser>> = ({ control, fieldName, filterState }) => (
    <ReactHookFormAutocompleteField
        control={control}
        label={filterState.label}
        name={fieldName}
        options={filterState.options}
    />
);

const IntegerField: FunctionComponent<FilterInput<FilterStateInteger>> = ({ control, fieldName, filterState }) => {
    const { component, label, options } = filterState;

    if (is.emptyArray(options)) {
        return null;
    }

    if (component === filterStateComponentMultiChipSelectLiteralSchema.value) {
        return (
            <ReactHookFormMultiChipSelectorField control={control} label={label} name={fieldName} options={options} />
        );
    }

    return <ReactHookFormAutocompleteField control={control} label={label} name={fieldName} options={options} />;
};

const DateField: FunctionComponent<FilterInput<FilterStateDate>> = ({ control, fieldName, filterState }) => {
    const { translate } = useContext(ApplicationContext);

    return (
        <Controller
            control={control}
            name={fieldName}
            render={({ field }) => {
                const { value } = field;

                const dateValue: DateRange<Date> = is.nonEmptyArray(value)
                    ? [is.date(value[0]) ? value[0] : null, is.date(value[1]) ? value[1] : null]
                    : [null, null];

                return (
                    <DateRangePicker
                        label={filterState.label}
                        labelEnd={translate('common.end')}
                        labelStart={translate('common.start')}
                        value={dateValue}
                        onChange={field.onChange}
                    />
                );
            }}
        />
    );
};

const RangeSliderTextField: FunctionComponent<{
    readonly label: string;
    readonly onChange: ChangeEventHandler<HTMLTextAreaElement>;
    readonly value: string;
}> = (field) => {
    const { label, value } = field;

    return (
        <TextField
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: 100, min: 0, step: 1 }}
            label={label}
            sx={{ gap: 3, height: '40px', width: '80px' }}
            type="number"
            value={value}
            variant="outlined"
            onChange={field.onChange}
        />
    );
};

const RangeSlider: FunctionComponent<FilterInput<FilterStatePercentage>> = ({ control, fieldName }) => (
    <Controller
        control={control}
        name={fieldName}
        render={({ field }) => {
            const { value } = field;
            const percentageRange = filterStatePercentageRangeSchema.safeParse(value);
            const [start, end] = percentageRange.success ? percentageRange.data : [0, 1];

            return (
                <Fragment>
                    <Slider
                        defaultValue={[start, end]}
                        marks={[
                            {
                                label: '0 %',
                                value: 0,
                            },
                            {
                                value: 0.25,
                            },
                            {
                                value: 0.5,
                            },
                            {
                                value: 0.75,
                            },
                            {
                                label: '100%',
                                value: 1,
                            },
                        ]}
                        max={1}
                        min={0}
                        step={0.01}
                        sx={{
                            '& .MuiSlider-markLabel[data-index="0"]': {
                                transform: 'translateX(0%)',
                            },
                            '& .MuiSlider-markLabel[data-index="4"]': {
                                transform: 'translateX(-100%)',
                            },
                        }}
                        value={[start, end]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${Math.round(value * 100)} %`}
                        onChange={field.onChange}
                    />
                    <Grid alignItems="center" container={true} flexDirection="row" gap={3} justifyContent="center">
                        <RangeSliderTextField
                            label="Min in %"
                            value={String(Math.round(start * 100))}
                            onChange={(event) => field.onChange([Number(event.target.value) / 100, end])}
                        />
                        -
                        <RangeSliderTextField
                            label="Max in %"
                            value={String(Math.round(end * 100))}
                            onChange={(event) => field.onChange([start, Number(event.target.value) / 100])}
                        />
                    </Grid>
                </Fragment>
            );
        }}
    />
);

// eslint-disable-next-line complexity
export const FilterInput: FunctionComponent<FilterInput> = ({ control, fieldName, filterState }) => {
    const { type } = filterState;

    if (type === 'boolean') {
        return <BooleanField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'string') {
        return <StringField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'user' && is.nonEmptyArray(filterState.options)) {
        return <UserField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'integer') {
        return <IntegerField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'date') {
        return <DateField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'skill') {
        return <SkillField control={control} fieldName={fieldName} filterState={filterState} />;
    }

    if (type === 'percent') {
        return <RangeSlider control={control} fieldName={fieldName} filterState={filterState} />;
    }

    return null;
};
